import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  HostListener,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { MunicipalRegulationsService } from './municipal-regulations.service';
import { SharedService } from 'src/app/common/services/shared.service';
import { ReportsChartEnum } from 'src/app/reports/models/enums/reports.enum';
import { Subscription } from 'rxjs';

type FloorKey = string;

@Component({
  selector: 'app-municipal-regulations',
  templateUrl: './municipal-regulations.component.html',
  styleUrls: ['./municipal-regulations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MunicipalRegulationsComponent implements OnInit, OnChanges {

  /* ================= INPUT / OUTPUT ================= */
  @Input() landParcelData: any[] = [];
  @Input() selectedParcelUid?: string;
  @Output() parcelChange = new EventEmitter<string>();

  /* ================= UI / STATE ================= */
  reportsChartEnum = ReportsChartEnum;

  parcels: { parcel_uid: string; parcel_number: string }[] = [];
  filteredParcels: { parcel_uid: string; parcel_number: string }[] = [];

  dropdownOpen = false;
  searchTerm = '';

  selectedParcelUidInternal = '';
  currentParcelData: any[] | null = null;
  loading = false;

  currentLang = 'en';

  private langSub?: Subscription;

  constructor(
    private service: MunicipalRegulationsService,
    private sharedService: SharedService,
    private cdr: ChangeDetectorRef
  ) {}


  floors: FloorKey[] = [
  'ground',
  'typical',
  'upper',
  'podium',
  'mezzanine',
  'basement',
  'refuge',
  'upper_annex'
];
  /* ================= LIFECYCLE ================= */

  ngOnInit(): void {
    // ✅ Language change → only UI update (NO API CALL)
    this.langSub = this.sharedService.language$.subscribe(lang => {
      this.currentLang = lang || 'en';
      this.cdr.markForCheck();
    });

    // Initial load
    // this.initParcelsOnce();
  }

  ngOnChanges(changes: SimpleChanges): void {


    // Selected parcel changed from parent
    if (
      changes['selectedParcelUid']?.currentValue &&
      changes['selectedParcelUid'].currentValue !== this.selectedParcelUidInternal
    ) {
      this.selectedParcelUidInternal = changes['selectedParcelUid'].currentValue;
      this.fetchMunicipalData();
    }
     // Parcel list changed (parent API) → initialize ONCE
    if (changes['landParcelData']?.currentValue) {
      this.initParcelsOnce();
    }
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  /* ================= INIT LOGIC ================= */

  private initParcelsOnce(): void {
    
    this.parcels = this.landParcelData.map(item => ({
      parcel_uid: item.parcel_uid,
      parcel_number: item.parcel_number || item.parcel_uid
    }));

    this.filteredParcels = this.parcels;
    // this.selectedParcelUidInternal =
    //   this.selectedParcelUid || this.parcels[0].parcel_uid;

    // this.fetchMunicipalData();
  }

  /* ================= DROPDOWN ================= */

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.searchTerm = '';
      this.filterParcels();
    }
  }


  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-select')) {
      this.dropdownOpen = false;
    }
  }

  selectParcel(uid: string): void {
    if (uid === this.selectedParcelUidInternal) {
      return;
    }

    this.selectedParcelUidInternal = uid;
    this.parcelChange.emit(uid);
    this.dropdownOpen = false;

    this.fetchMunicipalData();
  }

  getSelectedParcelName(): string {
    const selected = this.parcels.find(
      p => p.parcel_uid === this.selectedParcelUidInternal
    );
    return selected ? selected.parcel_number : '';
  }

  onSearchChange(): void {
    this.filterParcels();
  }

  private filterParcels(): void {
    if (!this.searchTerm.trim()) {
      this.filteredParcels = this.parcels;
      return;
    }

    const term = this.searchTerm.toLowerCase();
    this.filteredParcels = this.parcels.filter(p =>
      p.parcel_uid.toLowerCase().includes(term) ||
      p.parcel_number.toLowerCase().includes(term)
    );
  }

  /* ================= API ================= */

  private fetchMunicipalData(): void {
    if (!this.selectedParcelUidInternal) {
      return;
    }
    this.loading = true;
    this.currentParcelData = null;    
    this.cdr.markForCheck();

    this.service.getPermissibleUsageData(this.selectedParcelUidInternal).subscribe({
      next: data => {
        this.currentParcelData = data || null;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: err => {
        console.error('Municipal API Error:', err);
        this.currentParcelData = null;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /* ================= TABLE HELPERS ================= */

  trackByUsage(index: number): number {
    return index;
  }

hasFarExcluded(usage: any): boolean {
  return this.floors.some(floor => 
    usage[floor] > 0 && usage[floor + '_included'] === 'FALSE'
  );
}

  isNotSection(){
      return this.currentParcelData?.every(usage => usage.permitted_use_en == null) || false;
  }

  hasFloorData(usage: any): boolean {
    return this.floors.some(floor => usage[floor] > 0);
  }
  /* ================= LANGUAGE HELPERS ================= */

  getPermittedUse(usage: any): string {
    return (
      usage[`permitted_use_${this.currentLang}`] ||
      usage.permitted_use_en ||
      null
    );
  }

  getFloorRegulation(usage: any): string {
    return (
      usage[`floor_regulation_${this.currentLang}`] ||
      usage.floor_regulation_en ||
      ''
    );
  }

  getBuildingRegulation(usage: any): string {
    return (
      usage[`building_regulation_${this.currentLang}`] ||
      usage.building_regulation_en ||
      ''
    );
  }

  hasFloorRegulations(usage: any): boolean {
    return !!this.getFloorRegulation(usage).trim();
  }

  hasBuildingRegulations(usage: any): boolean {
    return !!this.getBuildingRegulation(usage).trim();
  }

  getRegulationItems(regulation: string): string[] {
    if (!regulation) return [];
    return regulation
      .split('*')
      .map(item => item.trim())
      .filter(Boolean);
  }

}
